import { useEffect, useState } from "react";
import { usePools } from "@/stores/pools.store";
import { useSwap } from "@/stores/swap.store";
import BigSwitch from "@/components/ui/BigSwitch";
import { DeDust, StonFi } from "./icons/PoolIcons";
import styles from "../Swap.module.scss";

const Pools = () => {
    const { pools, emulationResults, isLoading, poolUsdPrices } = usePools();
    const { selectedPool, setSelectedPool } = useSwap();
    const [selectedOption, setSelectedOption] = useState<string>("");

    // Ensure a pool is always selected when we have emulation results
    useEffect(() => {
        if (emulationResults && emulationResults.length > 0) {
            // If a pool is already selected in the store, sync it with our local state
            if (selectedPool && selectedPool !== selectedOption) {
                setSelectedOption(selectedPool);
            }
            // If no pool is selected but we have emulation results, select the first one
            else if (!selectedPool || !selectedOption) {
                const firstDex = emulationResults[0]?.dex;
                if (firstDex) {
                    const matchingPool = pools.find((pool) => pool.dex === firstDex);
                    if (matchingPool) {
                        setSelectedPool(matchingPool.dex);
                        setSelectedOption(matchingPool.dex);
                    }
                }
            }
        }
    }, [selectedPool, emulationResults, pools, setSelectedPool, selectedOption]);

    const handleOptionChange = (value: string) => {
        setSelectedOption(value);
        setSelectedPool(value);
    };

    // Transform pools data into options for the BigSwitch component
    const getOptions = () => {
        if (!pools) return [];

        return pools
            .filter((pool) => pool.dex !== "ston.fi_v1")
            .map((pool) => {
                const result = emulationResults?.find((r) => r.dex === pool.dex);
                const hasLiquidity = result && result.amount !== undefined;

                // Format USD price for display
                const priceData = poolUsdPrices[pool.poolAddress];
                let priceDisplay = null;

                if (priceData && priceData.buyPrice !== null) {
                    priceDisplay = `${priceData.buyPrice.toFixed(2)}`;
                }

                return {
                    value: pool.dex,
                    icon: pool.dex === "dedust" ? DeDust : StonFi,
                    label: isLoading ? (
                        <span className={styles.loadingText}>Loading...</span>
                    ) : hasLiquidity ? (
                        parseFloat(result.amount.toString()).toFixed(4)
                    ) : (
                        <span className={styles.noLiquidityText}>No liquidity</span>
                    ),
                    sublabel: priceDisplay,
                    disabled: isLoading || !hasLiquidity,
                };
            });
    };

    // If we have pool options but no selection, select the first one
    useEffect(() => {
        const options = getOptions();
        if (options.length > 0 && !selectedOption && !selectedPool) {
            const firstOption = options[0];
            setSelectedOption(firstOption.value);
            setSelectedPool(firstOption.value);
        }
    }, [pools, selectedOption, selectedPool, setSelectedPool, isLoading]);

    // Always provide a value to the BigSwitch, even if selectedOption is empty
    const effectiveValue = selectedOption || (getOptions().length > 0 ? getOptions()[0].value : "");

    if (!pools || pools.length === 0) {
        return (
            <div className={styles.loadingPoolsContainer}>
                <div className={styles.noPoolsAvailable}>
                    <span>No liquidity pools available</span>
                </div>
            </div>
        );
    }

    return (
        <BigSwitch options={getOptions()} value={effectiveValue} onValueChange={handleOptionChange} defaultValue={0} />
    );
};

export default Pools;
